import { useRef, type FC, Fragment } from 'react';
import { Card, CardMedia, CardContent, CardActionArea } from '@mui/material';
import { Flag } from '@packages/shared/src/components/Flag/Flag';
import { ProductPrice } from '@packages/shared/src/components/ProductPrice/ProductPrice';
import { Image } from '@packages/shared/src/components/Image/Image';
import { Link } from '@packages/modules/src/Link/Link';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import type {
  GTMEventGlycerinAdClick,
  GTMEventGlycerinProductClick,
} from '@packages/tracking/src/types/events';
import type {
  GlycerinAdClickPayload,
  GlycerinProductClickPayload,
} from '@packages/tracking/src/types/payloads';
import { getCanonicalUrl } from '@packages/shared/src/utils/getCanonicalUrl/getCanonicalUrl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { WishlistButtonWithApi } from '../WishlistButtonWithApi/WishlistButtonWithApi';
import { Title } from './Title';
import type { ProductCardProps, ProductCardFlag } from './types';
import { LegacyEnergyEfficiencyFlag } from '../LegacyEfficiencyFlag/LegacyEfficiencyFlag';

// Get image file name without extension from absolute URL
export const getImageFileName = (path: string): string => {
  const fileNameWithExtension = path.substring(path.lastIndexOf('/') + 1); // "file.txt"
  const fileName = fileNameWithExtension.split('.').slice(0, -1).join('.'); // "file"
  return fileName;
};

const getSortedFilteredFlags = ({
  minimal,
  flags,
}: {
  minimal: boolean;
  flags: ProductCardFlag[];
}) => {
  // sort flags by type
  const sortedFlags = flags.sort((a: ProductCardFlag, b: ProductCardFlag) => {
    const order = ['custom', 'sale', 'neutral', 'secondary', 'sustainability'];
    return order.indexOf(a.type || '') - order.indexOf(b.type || '');
  });

  const filteredFlags = sortedFlags.filter(
    (flag: ProductCardFlag) =>
      // minimal product card has no custom flag
      flag.type !== 'custom',
  );

  // minimal product card has only 2 flags, otherwise 3
  return minimal ? filteredFlags.slice(0, 2) : filteredFlags.slice(0, 3);
};

/**
 * ProductCard component based on https://mui.com/material-ui/react-card/
 * */
export const ProductCard: FC<ProductCardProps> = ({
  variationName,
  sku,
  productLink = '#',
  image,
  brand,
  title,
  price,
  minimal = true,
  flags,
  efficiencyFlags,
  datasheets,
  isSponsored,
  sx,
  onColorChange,
  onProductLinkActionAreaClick,
  useAdditionalFlagsSpacer,
  useRatingsSpacer,
  clickTrackingProps,
  wishListTrackingProps,
  role,
  ...rest
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const dispatchGTMEvent = useTracking();

  const px = [1];

  const handleActionAreaClick = () => {
    if (isSponsored) {
      // Sponsored Products click needs to be an adClick event
      dispatchGTMEvent<GTMEventGlycerinAdClick>({
        event: 'AdClick',
        AdClickData: {
          category: clickTrackingProps?.category ?? 'sponsored-product-click',
          adId: (clickTrackingProps as GlycerinAdClickPayload)?.adId,
          adType: (clickTrackingProps as GlycerinAdClickPayload)?.adType ?? 'sponsored product',
          target: (clickTrackingProps as GlycerinAdClickPayload)?.target ?? productLink,
          placement: clickTrackingProps?.placement,
          detail: clickTrackingProps?.detail,
          label: clickTrackingProps?.label,
          listPosition: clickTrackingProps?.listPosition,
          custom: clickTrackingProps?.custom,
          variantIds: clickTrackingProps?.variantIds,
        },
      });
    } else {
      // "regular" product click
      dispatchGTMEvent<GTMEventGlycerinProductClick>({
        event: 'ProductClick',
        ProductClickData: {
          category: clickTrackingProps?.category ?? 'product-click',
          productId: (clickTrackingProps as GlycerinProductClickPayload)?.productId,
          placement: clickTrackingProps?.placement,
          articleId: (clickTrackingProps as GlycerinProductClickPayload)?.articleId,
          detail: clickTrackingProps?.detail,
          imageType: (clickTrackingProps as GlycerinProductClickPayload)?.imageType,
          label: clickTrackingProps?.label,
          listId: (clickTrackingProps as GlycerinProductClickPayload)?.listId,
          listPosition: (clickTrackingProps as GlycerinProductClickPayload)?.listPosition,
          origin: (clickTrackingProps as GlycerinProductClickPayload)?.origin,
          recoId: (clickTrackingProps as GlycerinProductClickPayload)?.recoId,
          skuId: undefined,
          custom: clickTrackingProps?.custom,
          variantIds: clickTrackingProps?.variantIds,
        },
      });
    }
    if (onProductLinkActionAreaClick) {
      onProductLinkActionAreaClick();
    }
  };

  let linkTarget = productLink;
  const isBot = useIsBot();
  if (isBot) {
    linkTarget = getCanonicalUrl(productLink);
  }

  const titleWithBrand = `${brand} ${title}`;

  return (
    <div className="group relative h-full">
      <Link
        className="absolute inset-0 z-20 cursor-pointer"
        href={linkTarget}
        title={titleWithBrand}
        onClick={handleActionAreaClick}
      />
      {!isSponsored && (
        <WishlistButtonWithApi
          productName={titleWithBrand}
          sku={sku}
          size="small"
          variant="icon-flat"
          className="absolute top-0.5 right-0.5 z-50 w-auto px-1.5"
          availability="UNSELECTED"
          trackingProps={
            wishListTrackingProps
              ? {
                  products: [
                    {
                      // TODO: availability is currently pretty hard to map to the wishlist types, try to add it with or after the GraphQL migration
                      // availability: availability?.values?.[0]?.availabilityName,
                      currency:
                        price.currency === 'EUR' || price.currency === 'CHF'
                          ? price.currency
                          : 'EUR',
                      displayedPrice: price.price,
                      originalPrice: price.oldPrice,
                      ...wishListTrackingProps.product,
                    },
                  ],
                }
              : undefined
          }
        />
      )}
      <Card
        {...rest}
        ref={cardRef}
        title={titleWithBrand}
        className="group-hover:bg-color-grey-light"
        sx={{
          ...sx,
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div className="pointer-events-none relative z-20 w-full *:pointer-events-none">
          <div className="relative z-[1]">
            <div className="absolute inset-0 z-10 bg-[#000] opacity-0 group-hover:opacity-[0.04]" />
            {flags && (
              <div className="absolute top-0 left-0 z-[1] m-1 flex flex-col items-start">
                {getSortedFilteredFlags({ flags, minimal }).map((flag) => (
                  <Flag text={flag.text} type={flag.type} key={flag.text} />
                ))}
              </div>
            )}
            <CardMedia
              component="div"
              sx={{
                position: 'relative',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                '> img': {
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  px: 0.5,
                  pt: 0.5,
                },
              }}
            >
              <Image src={image} alt="" width={400} height={400} role={role} />
            </CardMedia>
          </div>
        </div>
        <div aria-live="polite" className="relative flex w-full flex-grow flex-col">
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              flexGrow: 1,
              px,
              py: 1,
            }}
          >
            <p className="text-color-text-darktransparent text-size-body-sm mb-1">{brand}</p>
            <Title minimal>{title}</Title>
            <ProductPrice {...price} hasDiscountFlag={!minimal} />
          </CardContent>

          {efficiencyFlags && (
            <>
              <Divider flexItem />
              <div className="flex gap-1 p-1">
                {efficiencyFlags.some((x) => x.datasheet) ? (
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 0,
                    }}
                  >
                    {efficiencyFlags.map((flagProps, index) => (
                      <Fragment key={flagProps.overlay?.title ?? index}>
                        <LegacyEnergyEfficiencyFlag {...flagProps} />
                        <div className="flex flex-col justify-center">
                          {efficiencyFlags.length > 1 && flagProps.overlay && (
                            <p className="text-size-body-xs font-semibold">
                              {flagProps.overlay.title}
                            </p>
                          )}
                          {flagProps.datasheet && (
                            <Link
                              href={flagProps.datasheet.url}
                              target="_blank"
                              // NOTE: currently there is no canonical way of having monochrome links, they default to primary (both text and decoration)
                              className="text-color-text-dark hover:text-color-grey-dark decoration-color-text-dark hover:decoration-color-grey-dark"
                            >
                              <p className="text-size-body-xs">{flagProps.datasheet.name}</p>
                            </Link>
                          )}
                        </div>
                      </Fragment>
                    ))}
                  </CardContent>
                ) : (
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 0,
                    }}
                  >
                    {efficiencyFlags.map((flagProps, index) => (
                      <LegacyEnergyEfficiencyFlag
                        {...flagProps}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                      />
                    ))}
                  </CardContent>
                )}
                {datasheets && datasheets.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {datasheets?.map((datasheet) => (
                      <CardActionArea
                        key={datasheet.url}
                        href={datasheet.url}
                        target="_blank"
                        sx={{ position: 'relative', zIndex: 20 }}
                      >
                        <CardContent
                          sx={{
                            color: 'text.dark',
                            typography: 'body3',
                            textDecoration: 'underline',
                            hyphens: 'auto',
                            px: 0,
                            py: 0,
                          }}
                        >
                          {datasheet.name}
                        </CardContent>
                      </CardActionArea>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {useAdditionalFlagsSpacer && !efficiencyFlags && (
            <div
              // +28px height for wrapping flags
              className={`p-0 ${datasheets?.length ? 'h-[71px]' : 'h-[53px]'}`}
            />
          )}
        </div>
      </Card>
    </div>
  );
};
